/**
 * AdminDashboard Component
 * Super-admin dashboard for managing users, clubs, and system health.
 * Extracted from App.js for better code organization.
 */

import React, { useState, useEffect } from 'react';
import { useConfirmDialog } from '../components/ui/ConfirmDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Utility function for authenticated API requests
const makeAuthenticatedRequest = async (url, options = {}) => {
  const token = localStorage.getItem('subbuteo_token') || sessionStorage.getItem('subbuteo_token');
  
  const config = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...options.headers
    }
  };

  try {
    const response = await fetch(url, config);
    
    if (response.status === 401) {
      console.warn('API call returned 401');
      return null;
    }
    
    return response;
  } catch (error) {
    console.error('API Request failed:', error);
    throw error;
  }
};

function AdminDashboard({ user, onLogout, onBackToApp }) {
  const { showConfirm } = useConfirmDialog();
  const [adminStats, setAdminStats] = useState({});
  const [allUsers, setAllUsers] = useState([]);
  const [allClubs, setAllClubs] = useState([]);
  const [recentRegistrations, setRecentRegistrations] = useState([]);
  const [systemHealth, setSystemHealth] = useState({});
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [promoCodeForm, setPromoCodeForm] = useState({
    code: '',
    discount_type: 'free_year',
    discount_value: 365,
    max_uses: 1,
    expires_days: 365,
    description: ''
  });
  
  // 🧹 CLEANUP STATES FOR TEST ACCOUNTS
  const [testAccounts, setTestAccounts] = useState([]);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [showCleanupConfirm, setShowCleanupConfirm] = useState(false);
  const [cleanupResults, setCleanupResults] = useState(null);

  useEffect(() => {
    // Only load admin data if user is actually an owner
    if (user?.plan_type === 'owner') {
      loadAdminData();
    } else {
      console.error('User is not an owner, cannot load admin data');
      setLoading(false);
      // Redirect back to main app if not owner
      setTimeout(() => {
        onBackToApp();
      }, 2000);
    }
  }, [user]);

  const loadAdminData = async () => {
    setLoading(true);
    try {
      // Load data with individual error handling to prevent complete failure
      const results = await Promise.allSettled([
        loadAdminStats(),
        loadAllUsers(),
        loadAllClubs(),
        loadRecentRegistrations(),
        loadSystemHealth()
      ]);
      
      // Log any failed requests for debugging
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          const endpoints = ['admin/stats', 'admin/users', 'admin/clubs', 'admin/recent-registrations', 'admin/system-health'];
          console.error(`Failed to load ${endpoints[index]}:`, result.reason);
        }
      });
    } catch (error) {
      console.error('Error loading admin data:', error);
      // Set fallback empty states to prevent UI crashes
      setAdminStats({});
      setAllUsers([]);
      setAllClubs([]);
      setRecentRegistrations([]);
      setSystemHealth({});
    }
    setLoading(false);
  };

  const loadAdminStats = async () => {
    try {
      const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/admin/stats`);
      if (response && response.ok) {
        const data = await response.json();
        setAdminStats(data);
      }
    } catch (error) {
      console.error('Error loading admin stats:', error);
    }
  };

  const loadAllUsers = async () => {
    try {
      const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/admin/users`);
      if (response && response.ok) {
        const data = await response.json();
        setAllUsers(data.users || []);
      }
    } catch (error) {
      console.error('Error loading all users:', error);
    }
  };

  const loadAllClubs = async () => {
    try {
      const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/admin/clubs`);
      if (response && response.ok) {
        const data = await response.json();
        setAllClubs(data.clubs || []);
      }
    } catch (error) {
      console.error('Error loading all clubs:', error);
    }
  };

  const loadRecentRegistrations = async () => {
    try {
      const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/admin/recent-registrations?days=30`);
      if (response && response.ok) {
        const data = await response.json();
        setRecentRegistrations(data.recent_registrations || []);
      }
    } catch (error) {
      console.error('Error loading recent registrations:', error);
    }
  };

  const loadSystemHealth = async () => {
    try {
      const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/admin/system-health`);
      if (response && response.ok) {
        const data = await response.json();
        setSystemHealth(data);
      }
    } catch (error) {
      console.error('Error loading system health:', error);
    }
  };

  const createPromoCode = async () => {
    try {
      const payload = {
        code: promoCodeForm.code.toUpperCase(),
        discount_type: promoCodeForm.discount_type,
        discount_value: promoCodeForm.discount_value,
        max_uses: promoCodeForm.max_uses,
        description: promoCodeForm.description,
        expires_at: new Date(Date.now() + promoCodeForm.expires_days * 24 * 60 * 60 * 1000).toISOString()
      };
      
      const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/admin/promo-codes`, {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      
      if (response && response.ok) {
        alert('Codice promozionale creato con successo!');
        setPromoCodeForm({
          code: '',
          discount_type: 'free_year',
          discount_value: 365,
          max_uses: 1,
          expires_days: 365,
          description: ''
        });
        loadAdminStats(); // Refresh stats
      } else {
        const error = await response.json();
        alert('Errore: ' + error.detail);
      }
    } catch (error) {
      console.error('Error creating promo code:', error);
      alert('Errore nella creazione del codice promozionale');
    }
  };

  // 🧹 CLEANUP FUNCTIONS
  const previewTestAccounts = async () => {
    setCleanupLoading(true);
    try {
      const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/admin/preview-test-accounts`);
      if (response && response.ok) {
        const data = await response.json();
        setTestAccounts(data.test_accounts || []);
        setShowCleanupConfirm(true);
      }
    } catch (error) {
      console.error('Error previewing test accounts:', error);
    }
    setCleanupLoading(false);
  };

  const cleanupTestAccounts = async () => {
    setCleanupLoading(true);
    try {
      const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/admin/cleanup-test-accounts`, {
        method: 'DELETE'
      });
      if (response && response.ok) {
        const data = await response.json();
        setCleanupResults(data);
        setShowCleanupConfirm(false);
        loadAdminData(); // Refresh all data
      }
    } catch (error) {
      console.error('Error cleaning up test accounts:', error);
    }
    setCleanupLoading(false);
  };

  // 🗑️ DELETE CLUB
  const deleteClub = async (clubId, clubName) => {
    showConfirm({
      title: 'Elimina Club',
      message: `Sei sicuro di voler eliminare il club "${clubName}"? Questa azione è irreversibile e eliminerà tutti i dati associati (membri, tornei, finanze, etc).`,
      confirmText: '🗑️ Elimina Definitivamente',
      cancelText: 'Annulla',
      variant: 'danger',
      onConfirm: async () => {
        try {
          const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/admin/clubs/${clubId}`, {
            method: 'DELETE'
          });
          
          if (response && response.ok) {
            loadAllClubs(); // Refresh clubs list
          } else {
            const error = await response.json();
            alert('Errore: ' + error.detail);
          }
        } catch (error) {
          console.error('Error deleting club:', error);
          alert('Errore durante l\'eliminazione del club');
        }
      }
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
          <p className="text-gray-300">Caricamento dashboard amministratore...</p>
        </div>
      </div>
    );
  }

  // Security check - verify user is owner
  if (user?.plan_type !== 'owner') {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center text-red-500">
          <h1 className="text-2xl font-bold mb-4">⚠️ Accesso Negato</h1>
          <p>Solo l'amministratore può accedere a questa pagina.</p>
          <button 
            onClick={onBackToApp}
            className="mt-4 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
          >
            Torna all'App
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white" data-testid="admin-dashboard">
      {/* Header */}
      <div className="bg-gray-800 shadow-lg border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <span className="text-2xl">🔐</span>
            <h1 className="text-xl font-bold text-green-400">Admin Dashboard</h1>
            <span className="text-sm text-gray-400">({user?.email})</span>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={onBackToApp}
              className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-500"
              data-testid="back-to-app-btn"
            >
              ← Torna all'App
            </button>
            <button
              onClick={onLogout}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              data-testid="admin-logout-btn"
            >
              🚪 Logout
            </button>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex space-x-4">
            {['dashboard', 'users', 'clubs', 'promo', 'system', 'cleanup'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-3 text-sm font-medium ${
                  activeTab === tab
                    ? 'text-green-400 border-b-2 border-green-400'
                    : 'text-gray-400 hover:text-gray-200'
                }`}
                data-testid={`tab-${tab}`}
              >
                {tab === 'dashboard' && '📊 Dashboard'}
                {tab === 'users' && '👥 Utenti'}
                {tab === 'clubs' && '🏢 Club'}
                {tab === 'promo' && '🎁 Promo Codes'}
                {tab === 'system' && '🔧 Sistema'}
                {tab === 'cleanup' && '🧹 Pulizia'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
              <div className="text-3xl font-bold text-green-400">{adminStats.total_users || 0}</div>
              <div className="text-sm text-gray-400">Utenti Totali</div>
            </div>
            <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
              <div className="text-3xl font-bold text-blue-400">{adminStats.active_subscriptions || 0}</div>
              <div className="text-sm text-gray-400">Abbonamenti Attivi</div>
            </div>
            <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
              <div className="text-3xl font-bold text-yellow-400">{adminStats.premium_subscriptions || 0}</div>
              <div className="text-sm text-gray-400">Premium</div>
            </div>
            <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
              <div className="text-3xl font-bold text-purple-400">{adminStats.total_matches || 0}</div>
              <div className="text-sm text-gray-400">Partite Totali</div>
            </div>
            <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
              <div className="text-3xl font-bold text-pink-400">€{adminStats.estimated_revenue?.toFixed(2) || '0.00'}</div>
              <div className="text-sm text-gray-400">Revenue Stimata</div>
            </div>
            <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
              <div className="text-3xl font-bold text-cyan-400">{adminStats.recent_registrations || 0}</div>
              <div className="text-sm text-gray-400">Registrazioni (7gg)</div>
            </div>
            <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
              <div className="text-3xl font-bold text-orange-400">{adminStats.trial_users || 0}</div>
              <div className="text-sm text-gray-400">Trial Attivi</div>
            </div>
            <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
              <div className="text-3xl font-bold text-red-400">{adminStats.promo_codes?.total || 0}</div>
              <div className="text-sm text-gray-400">Promo Codes</div>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-700 flex justify-between items-center">
              <h2 className="font-semibold text-lg">👥 Tutti gli Utenti ({allUsers.length})</h2>
              <button
                onClick={loadAllUsers}
                className="px-3 py-1 bg-gray-700 rounded hover:bg-gray-600 text-sm"
              >
                🔄 Aggiorna
              </button>
            </div>
            <div className="overflow-x-auto max-h-96">
              <table className="w-full text-sm">
                <thead className="bg-gray-700 sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left">Email</th>
                    <th className="px-4 py-2 text-left">Nome</th>
                    <th className="px-4 py-2 text-left">Piano</th>
                    <th className="px-4 py-2 text-left">Partite</th>
                    <th className="px-4 py-2 text-left">Registrato</th>
                  </tr>
                </thead>
                <tbody>
                  {allUsers.map((u, i) => (
                    <tr key={i} className="border-b border-gray-700 hover:bg-gray-750">
                      <td className="px-4 py-2">{u.email}</td>
                      <td className="px-4 py-2">{u.name}</td>
                      <td className="px-4 py-2">
                        <span className={`px-2 py-1 rounded text-xs ${
                          u.plan_type === 'owner' ? 'bg-purple-600' :
                          u.plan_type === 'premium' ? 'bg-yellow-600' :
                          u.plan_type === 'trial' ? 'bg-green-600' :
                          u.plan_type === 'trial_expired' ? 'bg-orange-600' :
                          u.plan_type === 'expired' ? 'bg-red-600' :
                          'bg-gray-600'
                        }`}>
                          {u.plan_type === 'owner' ? '👑 Owner' :
                           u.plan_type === 'premium' ? '⭐ Premium' :
                           u.plan_type === 'trial' ? `🕐 Trial (${u.days_remaining || 0}gg)` :
                           u.plan_type === 'trial_expired' ? '⏰ Trial Scaduto' :
                           u.plan_type === 'expired' ? '❌ Scaduto' :
                           '🆓 Free'}
                        </span>
                      </td>
                      <td className="px-4 py-2">{u.match_count || 0}</td>
                      <td className="px-4 py-2 text-gray-400 text-xs">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString('it-IT') : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Clubs Tab */}
        {activeTab === 'clubs' && (
          <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-700 flex justify-between items-center">
              <h2 className="font-semibold text-lg">🏢 Tutti i Club ({allClubs.length})</h2>
              <button
                onClick={loadAllClubs}
                className="px-3 py-1 bg-gray-700 rounded hover:bg-gray-600 text-sm"
              >
                🔄 Aggiorna
              </button>
            </div>
            <div className="overflow-x-auto max-h-96">
              <table className="w-full text-sm">
                <thead className="bg-gray-700 sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left">Nome Club</th>
                    <th className="px-4 py-2 text-left">Username</th>
                    <th className="px-4 py-2 text-left">Piano</th>
                    <th className="px-4 py-2 text-left">Membri</th>
                    <th className="px-4 py-2 text-left">Tornei</th>
                    <th className="px-4 py-2 text-left">Eventi</th>
                    <th className="px-4 py-2 text-left">Registrato</th>
                    <th className="px-4 py-2 text-left">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {allClubs.map((club, i) => (
                    <tr key={i} className="border-b border-gray-700 hover:bg-gray-750">
                      <td className="px-4 py-2">{club.name || club.club_name || '-'}</td>
                      <td className="px-4 py-2">{club.username}</td>
                      <td className="px-4 py-2">
                        <span className={`px-2 py-1 rounded text-xs ${
                          club.plan_type === 'premium' ? 'bg-yellow-600' :
                          club.plan_type === 'trial' ? 'bg-green-600' :
                          club.plan_type === 'trial_expired' ? 'bg-orange-600' :
                          club.plan_type === 'expired' ? 'bg-red-600' :
                          'bg-gray-600'
                        }`}>
                          {club.plan_type === 'premium' ? '⭐ Premium' :
                           club.plan_type === 'trial' ? `🕐 Trial (${club.days_remaining || 0}gg)` :
                           club.plan_type === 'trial_expired' ? '⏰ Trial Scaduto' :
                           club.plan_type === 'expired' ? '❌ Scaduto' :
                           '🆓 Free'}
                        </span>
                      </td>
                      <td className="px-4 py-2">{club.members_count || 0}</td>
                      <td className="px-4 py-2">{club.tournaments_count || 0}</td>
                      <td className="px-4 py-2">{club.events_count || 0}</td>
                      <td className="px-4 py-2 text-gray-400 text-xs">
                        {club.created_at ? new Date(club.created_at).toLocaleDateString('it-IT') : '-'}
                      </td>
                      <td className="px-4 py-2">
                        <button
                          onClick={() => deleteClub(club.id, club.name || club.club_name || club.username)}
                          className="px-2 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700"
                          data-testid={`delete-club-${club.id}`}
                        >
                          🗑️ Elimina
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Promo Codes Tab */}
        {activeTab === 'promo' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h2 className="font-semibold text-lg mb-4">🎁 Crea Nuovo Codice Promo</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Codice</label>
                  <input
                    type="text"
                    value={promoCodeForm.code}
                    onChange={(e) => setPromoCodeForm({...promoCodeForm, code: e.target.value.toUpperCase()})}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                    placeholder="PROMO2024"
                    data-testid="promo-code-input"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Tipo Sconto</label>
                  <select
                    value={promoCodeForm.discount_type}
                    onChange={(e) => setPromoCodeForm({...promoCodeForm, discount_type: e.target.value})}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                    data-testid="promo-type-select"
                  >
                    <option value="free_year">Anno Gratuito</option>
                    <option value="free_month">Mese Gratuito</option>
                    <option value="percentage">Percentuale</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Usi Massimi</label>
                  <input
                    type="number"
                    value={promoCodeForm.max_uses}
                    onChange={(e) => setPromoCodeForm({...promoCodeForm, max_uses: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                    min="1"
                    data-testid="promo-uses-input"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Descrizione</label>
                  <input
                    type="text"
                    value={promoCodeForm.description}
                    onChange={(e) => setPromoCodeForm({...promoCodeForm, description: e.target.value})}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                    placeholder="Codice promo speciale..."
                    data-testid="promo-desc-input"
                  />
                </div>
                <button
                  onClick={createPromoCode}
                  disabled={!promoCodeForm.code}
                  className="w-full py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                  data-testid="create-promo-btn"
                >
                  ✅ Crea Codice
                </button>
              </div>
            </div>
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h2 className="font-semibold text-lg mb-4">📊 Statistiche Promo</h2>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-400">Codici Totali</span>
                  <span className="font-bold">{adminStats.promo_codes?.total || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Codici Utilizzati</span>
                  <span className="font-bold">{adminStats.promo_codes?.used || 0}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* System Tab */}
        {activeTab === 'system' && (
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <h2 className="font-semibold text-lg mb-4">🔧 Salute del Sistema</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h3 className="text-green-400 font-medium mb-2">Database</h3>
                <div className="bg-gray-700 rounded p-3">
                  <div className="flex justify-between mb-2">
                    <span>Stato</span>
                    <span className={systemHealth.database?.status === 'connected' ? 'text-green-400' : 'text-red-400'}>
                      {systemHealth.database?.status || 'Unknown'}
                    </span>
                  </div>
                  {systemHealth.database?.collections && Object.entries(systemHealth.database.collections).map(([name, info]) => (
                    <div key={name} className="flex justify-between text-sm text-gray-400">
                      <span>{name}</span>
                      <span>{info.count} documenti</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="text-blue-400 font-medium mb-2">Integrazioni</h3>
                <div className="bg-gray-700 rounded p-3">
                  <div className="flex justify-between mb-2">
                    <span>PayPal</span>
                    <span className={systemHealth.integrations?.paypal?.status === 'configured' ? 'text-green-400' : 'text-yellow-400'}>
                      {systemHealth.integrations?.paypal?.status || 'Unknown'} ({systemHealth.integrations?.paypal?.mode})
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Email</span>
                    <span className={systemHealth.integrations?.email?.status === 'configured' ? 'text-green-400' : 'text-yellow-400'}>
                      {systemHealth.integrations?.email?.status || 'Unknown'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Cleanup Tab */}
        {activeTab === 'cleanup' && (
          <>
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <h2 className="font-semibold text-lg mb-4">🧹 Pulizia Account di Test</h2>
            <p className="text-gray-400 mb-4">
              Questa funzione identifica e rimuove gli account di test basandosi su pattern specifici 
              (es. email contenenti "test@", "@example.com", ecc.)
            </p>
            
            {cleanupResults && (
              <div className="mb-4 p-4 bg-green-900/30 border border-green-700 rounded">
                <h3 className="text-green-400 font-medium">✅ Pulizia Completata</h3>
                <p className="text-gray-300">{cleanupResults.message}</p>
                <p className="text-sm text-gray-400">Account eliminati: {cleanupResults.deleted_accounts}</p>
              </div>
            )}
            
            <button
              onClick={previewTestAccounts}
              disabled={cleanupLoading}
              className="px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700 disabled:opacity-50"
              data-testid="preview-test-accounts-btn"
            >
              {cleanupLoading ? 'Caricamento...' : '👀 Anteprima Account di Test'}
            </button>
          </div>
          
          {/* Cleanup Confirmation Modal */}
          {showCleanupConfirm && (
            <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
              <div className="bg-gray-800 rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
                <h3 className="text-xl font-bold text-red-400 mb-4">⚠️ Conferma Eliminazione</h3>
                <p className="text-gray-300 mb-4">
                  Trovati <span className="font-bold text-yellow-400">{testAccounts.length}</span> account di test:
                </p>
                
                <div className="max-h-60 overflow-y-auto mb-4 bg-gray-900 rounded p-2">
                  {testAccounts.map((account, i) => (
                    <div key={i} className="text-sm py-1 border-b border-gray-700 last:border-b-0">
                      <span className="text-gray-300">{account.email}</span>
                      <span className="text-gray-500 ml-2">({account.name})</span>
                      <span className="text-xs text-gray-600 ml-2">[{account.matched_patterns?.join(', ')}]</span>
                    </div>
                  ))}
                </div>
                
                <div className="flex justify-center space-x-4">
                  <button
                    onClick={() => setShowCleanupConfirm(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                  >
                    Annulla
                  </button>
                  <button
                    onClick={cleanupTestAccounts}
                    disabled={cleanupLoading}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:opacity-50"
                  >
                    {cleanupLoading ? 'Eliminando...' : '🗑️ Elimina Definitivamente'}
                  </button>
                </div>
              </div>
            </div>
          )}
          </>
        )}
      </div>
    </div>
  );
}

export default AdminDashboard;
